import {
  DropdownMenu,
  DropdownMenuItem,
} from '@/components/Elements/DropdownMenu';
import { Option, SelectField } from '@/components/Form/SelectField';
import { IconChevronDown, IconWorld } from '@tabler/icons-react';
import clsx from 'clsx';
import { getCurrentLanguage } from '@/lib/i18n/getCurrentLanguage';
import { type SupportedLanguage } from '@/lib/i18n/config';
import {
  getLanguageLabel,
  getLanguageOptions,
} from '@/lib/i18n/languageLabels';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';

type Props = {
  theme?: 'black' | 'white';
  variant?: 'header' | 'settings' | 'login';
};

export const LanguageSwitcher = ({
  theme = 'black',
  variant = 'header',
}: Props) => {
  useTranslation();

  const currentLanguageKey = getCurrentLanguage();
  const currentLanguage = getLanguageLabel(currentLanguageKey);
  const languageOptions = getLanguageOptions();

  const handleLanguageChange = (languageKey: SupportedLanguage) => {
    i18n.changeLanguage(languageKey);
  };

  // 設定画面用のシンプルなプルダウン
  if (variant === 'settings') {
    return (
      <DropdownMenu
        trigger={
          <button
            type="button"
            className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2"
          >
            <span>{currentLanguage}</span>
            <IconChevronDown className="size-4 text-gray-500" />
          </button>
        }
        className="z-50"
      >
        {languageOptions.map((option) => (
          <DropdownMenuItem
            key={option.value}
            onClick={() => handleLanguageChange(option.value)}
          >
            {option.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenu>
    );
  }

  if (variant === 'login') {
    return (
      <div className="w-[131px]">
        <SelectField<Option<SupportedLanguage>, false>
          aria-label={currentLanguage}
          appearance="design-system"
          designSystemSize="md"
          isSearchable={false}
          options={languageOptions}
          value={languageOptions.find(
            (option) => option.value === currentLanguageKey
          )}
          onChange={(option) => {
            if (option) handleLanguageChange(option.value);
          }}
          formatOptionLabel={(option, { context }) =>
            context === 'value' ? (
              <span className="flex min-w-0 items-center gap-1.5">
                <IconWorld className="size-5 shrink-0 stroke-[1.5]" />
                <span className="truncate">{option.label}</span>
              </span>
            ) : (
              option.label
            )
          }
        />
      </div>
    );
  }

  // ヘッダー用の元のデザイン
  return (
    <DropdownMenu
      trigger={
        <div
          className={clsx(
            'relative flex cursor-default items-center gap-x-1.5 rounded-2xl px-3 py-1.5 ring-1 ring-inset',
            theme === 'black' ? 'ring-black' : 'text-white ring-white'
          )}
        >
          <IconWorld className="hidden size-6 sm:block" />
          {currentLanguage}
          <IconChevronDown className="size-3" />
        </div>
      }
      className="z-50"
      matchTriggerWidth
    >
      {languageOptions.map((option) => (
        <DropdownMenuItem
          key={option.value}
          onClick={() => handleLanguageChange(option.value)}
        >
          {option.label}
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
};
